import type { GetServerSideProps } from 'next';
import { queryCmsContent } from '$server/modules/cms';
import MainLayout from '$layout/MainLayout';
import { QUERY_GET_FOOTER_CMS_CONTENT } from '$components/layout/Footer/gql/queries/getFooterCmsContent';
import type { QueryReadFooterData } from '$components/layout/Footer/gql/queries/readFooterData';
import { QUERY_READ_FOOTER_DATA } from '$components/layout/Footer/gql/queries/readFooterData';
import { getNavbarQuery } from '$commons/gql';
import { getVrmLookupQuery } from '$quantum/molecules/VrmLookup/gql';
import { getTeamSupportSection } from '$quantum/organisms/TeamSupportSection/gql';
import { getPromotionBannerQuery } from '$quantum/molecules/PromotionBanner/gql';
import deferHydration from '$util/deferHydration';
import { useAppContext } from '$util/AppContext';
import type { AppContextGlobal } from '$util/AppContextGlobal';
import type { CmsContentProps } from '$cms/index';
import { getImageByKey, getShortTextByKey } from '$cms/getDataByKey';
import { GET_CUSTOMER_SERVICE_CMS_QUERY } from '$cms/gql';
import { PageTitle } from '$components/pages/contact-us/components/PageTitle';
import { HereToHelp } from '$quantum/molecules/HereToHelp';
import { GET_HOMEPAGE_OPEN_GRAPH_CONTENT, GET_DOMAIN_VERIFICATION_CONTENT } from '$layout/MainLayout/gql';
import { getMetaTitle } from '$components/pages/gql';
import { isPreviewActive } from '$server/modules/cms/util';
import { getLocaleFromHostname } from '$util/domains';
import { CACHE_CONTROL_CACHE_ENABLED, CACHE_CONTROL_NO_CACHE } from '../internals/next/config/headers';

const PAGE_NAME = 'contact-us';

const TeamSupportSection = deferHydration(() => import('$quantum/organisms/TeamSupportSection'), {
  hydrationId: '$components/TeamSupportSection',
  trigger: 'view',
});

/**
 * CMS data transformed in typePolicies
 */
type CmsContentReadData = QueryReadFooterData;

interface ContactUsPageProps extends CmsContentProps<CmsContentReadData> {
  initChat: boolean;
}

function ContactUsPage(): JSX.Element {
  const {
    cmsContent: { dictionary },
  } = useAppContext<AppContextGlobal>();

  const getShortText = getShortTextByKey.bind(dictionary);
  const getImage = getImageByKey.bind(dictionary);
  const pageTitle = getShortText('metaTitle.contactUs', 'Contact us | Fixter');
  const pageMetaTitle = getShortText('teamSupport.contactUs.pageTitle', 'Contact us | Fixter');
  const hereToHelpText = getShortText('teamSupport.hereToHelp.text', "We're here to help");
  const hereToHelpImage = getImage('teamSupport.hereToHelp.image', {
    img: { original_secure_url: '/icons/icon_wave_z4nqv4.svg', width: 22, height: 22 },
    alt: 'wave',
    title: '',
  });
  const teamSupportContent = getShortText(
    'teamSupport.content',
    'Our in-house experts know cars, our garages and our drivers inside out, so can help with any questions you may have.'
  );

  const hereToHelp = <HereToHelp text={hereToHelpText} image={hereToHelpImage} />;

  return (
    <MainLayout
      seo={{
        noindex: false,
        nofollow: false,
        title: pageMetaTitle,
      }}
      className="ContactUsPage"
    >
      <PageTitle pageTitle={pageTitle} />
      <TeamSupportSection title={hereToHelp} content={<span>{teamSupportContent}</span>} source={PAGE_NAME} />
    </MainLayout>
  );
}

export const getServerSideProps: GetServerSideProps<ContactUsPageProps> = async ({
  req,
  res,
  query: urlQuery,
  preview: isPreviewMode,
}) => {
  const serverParams = {
    pathname: 'contact-us',
    pageType: [],
    pageTypeFromPath: 'contact-us',
  };
  const locale = getLocaleFromHostname(req.headers.host || '');

  const preview = isPreviewActive(urlQuery, isPreviewMode);
  const cmsContent = await queryCmsContent(PAGE_NAME, {
    cmsQuery: [
      GET_HOMEPAGE_OPEN_GRAPH_CONTENT,
      GET_DOMAIN_VERIFICATION_CONTENT,
      getNavbarQuery,
      getVrmLookupQuery,
      QUERY_GET_FOOTER_CMS_CONTENT,
      GET_CUSTOMER_SERVICE_CMS_QUERY,
      getTeamSupportSection,
      getPromotionBannerQuery,
      getMetaTitle,
    ],
    resQuery: [QUERY_READ_FOOTER_DATA],
    context: {
      variables: { locale },
      preview,
    },
  });

  if (preview) res.setHeader('Cache-Control', CACHE_CONTROL_NO_CACHE);
  else res.setHeader('Cache-Control', CACHE_CONTROL_CACHE_ENABLED);

  return {
    props: { serverParams, initChat: false, cmsContent },
  };
};

export default ContactUsPage;
